import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { StaticContext } from 'react-router';
import omit from 'lodash/omit';

import {
    getDoc,
    getPatientByLocalId,
    getPrescriptionById,
    upsertPrescription,
} from '../store/idbHandler';

import { BorrowPrescriptionRouterProps, GO_TO_PRESCRIPTION_PAD } from '../routes/paths';
import { GlobalState } from '../store/reducers';
import { CreateVisitAction, initVisit } from '../store/actions/visit';
import fetchPrescriptionById, { Px } from '../api/fetchPrescriptionById';
import moment from 'moment';
import { Followup } from '../store/types/prescription';
import { medWithQuantity, sanitizeMed } from '../store/utils';

const BORROW = 'BORROW';

export interface BorrowPrescriptionLocationState {
    intoPxId?: string;
}

interface Props
    extends RouteComponentProps<
        BorrowPrescriptionRouterProps,
        StaticContext,
        BorrowPrescriptionLocationState | undefined
    > {
    dispatch: ThunkDispatch<GlobalState, null, CreateVisitAction>;
}

class BorrowPrescription extends PureComponent<Props> {
    componentDidMount = async (): Promise<void> => {
        if (!this.props.match.params.pxId) {
            console.error('pxId not passed');
            this.props.history.goBack();
            return;
        }

        let px: Px | undefined;

        try {
            px = await fetchPrescriptionById(this.props.match.params.pxId);
        } catch (e) {
            console.error(e);
        }

        if (!px) {
            console.error('px not found');
            this.props.history.goBack();
            return;
        }

        const pt = await getPatientByLocalId(this.props.match.params.plId);

        if (!pt) {
            console.error('pt not found');
            this.props.history.goBack();
            return;
        }

        const clinicId =
            px.selectedClinicData?._id || getDoc()?.profile.professional.default_clinic;

        if (!clinicId) {
            console.error('clinicId not found');
            this.props.history.goBack();
            return;
        }

        const onSuccess = (prescriptionId: string) => {
            this.props.history.replace(GO_TO_PRESCRIPTION_PAD(prescriptionId, 'edit'));
        };

        if (this.props.location?.state?.intoPxId) {
            const localPx = await getPrescriptionById(this.props.location.state?.intoPxId);

            if (!localPx) {
                console.error("Can't find local Px to update");
                return;
            }

            await upsertPrescription({
                ...localPx,
                symptoms: px.tool.symptoms?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                diagnosis: px.tool.diagnosis?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                injections: px.tool.injections?.map((injection) => ({
                    ...injection,
                    track: {
                        source: BORROW,
                    },
                })),
                medications: px.tool.medications?.map((s) => ({
                    ...medWithQuantity(sanitizeMed(s)),
                    track: {
                        source: BORROW,
                    },
                })),
                labTests: px.tool.labTests?.map((s) => ({
                    ...omit(s, ['test_on', 'repeat_on']),
                    track: {
                        source: BORROW,
                    },
                })),
                labVitals: px.tool.labVitals?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                followup: computeFollowUp(px.date, px.tool.followup),
                advices: px.tool.advices?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                dentalExaminations: px.tool.dentalExaminations?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                dentalProcedures: px.tool.dentalProcedures?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                opVision: px.tool.opVision?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                opIop: px.tool.opIop?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                opSubjectiveRefraction: px.tool.opSubjectiveRefraction?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                opAutoRefraction: px.tool.opAutoRefraction?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                opCurrentSpec: px.tool.opCurrentSpec?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                opFinalPrescription: px.tool.opFinalPrescription?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                procedures: px.tool.procedures?.map((s) => ({
                    ...s,
                    track: {
                        source: BORROW,
                    },
                })),
                careCanvas: px.tool.careCanvas,
            });

            onSuccess(this.props.location?.state?.intoPxId);
            return;
        }

        const initVisitAction = initVisit(
            {
                id: px.patient._id,
                localId: px.patient._id,
                uuid: px.patient.uuid || pt.uuid,
                name: [pt.personal.name.f, pt.personal.name.l].filter(Boolean).join(' '),
                phCode: pt.personal.phone?.c,
                phoneNumber: pt.personal.phone?.n,
                age: pt.personal.age,
                gender: pt.personal.gender.toString(),
                onApp: pt.personal.onApp,
                formData: pt.formData,
                abha: Array.isArray(pt.abhaV2)
                    ? pt.abhaV2?.find((abha) => abha.cId === clinicId)
                    : pt?.abha,
                relations: pt.relations,
            },
            px.doctor._id,
            clinicId,
            undefined,
            onSuccess,
            {
                ...px,
                tool: {
                    ...px.tool,
                    medications: px.tool.medications?.map((t) => medWithQuantity(sanitizeMed(t))),
                },
            },
            { autoCopy: false },
        );
        initVisitAction && this.props.dispatch(initVisitAction);
    };

    render() {
        return (
            <div className="flex flex-col items-center justify-center w-screen h-screen space-y-64">
                <div className="pulse">
                    <svg width="16px" height="12px">
                        <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                    </svg>
                </div>
            </div>
        );
    }
}

export default connect()(BorrowPrescription);

export const computeFollowUp = (pxDate: string, followup?: Followup): Followup | undefined => {
    const today = moment(new Date());

    let date: string | undefined = undefined;

    if (followup?.date && moment(followup?.date).isValid()) {
        const diff = moment(followup?.date).diff(pxDate, 'days');
        if (diff >= 1) {
            date = today.add(diff, 'days').toISOString();
        }
    }

    return {
        date: date,
        notes: followup?.notes,
    };
};
